import type { ReactNode } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/modern/components/ui/card-shadcn";
import { cn } from "@/modern/utils";

export const VIEW_EDITOR_SHELL_CLASS = "text-sm font-normal";

/**
 * Unified 36px controls. Uses ! to override h-swivu-input (~2.73rem) on Input/Select/Button primitives.
 */
export const VIEW_EDITOR_CONTROL_CLASS =
  "!h-9 !min-h-9 !max-h-9 box-border text-sm leading-none border border-input bg-background rounded-md shadow-none px-3 py-0";

/** Ensures every control in a field slot matches height (Select trigger, buttons, inputs). */
export const VIEW_EDITOR_FIELD_CONTROL_SLOT_CLASS =
  "[&_input]:!h-swivu-input [&_input]:!min-h-swivu-input [&_button]:!h-swivu-input [&_button]:!min-h-swivu-input [&_button]:!py-0";

export const VIEW_EDITOR_FIELD_GRID_CLASS = "grid grid-cols-2 gap-x-4 gap-y-3";

export const VIEW_EDITOR_SECTION_STACK_CLASS = "space-y-4";

interface ViewEditorSectionProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
  /** Stretch section and children to fill remaining tab height */
  fillHeight?: boolean;
}

export const ViewEditorSection = ({
  title,
  subtitle,
  children,
  className,
  fillHeight = false
}: ViewEditorSectionProps) => (
  <Card
    variant="modern"
    className={cn(
      "rounded-lg border border-border/50 shadow-none overflow-hidden",
      fillHeight && "flex flex-1 flex-col min-h-0 h-full",
      className
    )}
  >
    <CardHeader className="px-5 pt-4 pb-0 space-y-1">
      <CardTitle className="text-sm font-normal leading-tight">{title}</CardTitle>
      {subtitle && (
        <CardDescription className="text-xs leading-snug">{subtitle}</CardDescription>
      )}
    </CardHeader>
    <CardContent
      className={cn(
        "px-5 pb-4 pt-4",
        fillHeight && "flex flex-1 flex-col min-h-0 overflow-hidden"
      )}
    >
      <div
        className={cn(
          fillHeight
            ? "flex flex-1 flex-col min-h-0 h-full"
            : "space-y-4"
        )}
      >
        {children}
      </div>
    </CardContent>
  </Card>
);
